import React, { useEffect, useState, useMemo } from "react";
import { Typography, Button, Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import FormField from "../FormComponent/FormField/FormField";
import styles from './practitioner.module.css'
import { useForm } from 'react-hook-form';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { axiosProxyInstance } from "../../utils/request";
import cogoToast from "cogo-toast";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

const defaultValidationSchema = yup.object({
  'openingTime-timeSlot-1': yup
    .string()
    .required('Opening time is required')
    .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
    .test('is-before-closing', 'Opening time must be before closing time', function (value) {
      const { closingTime } = this.parent;
      if (closingTime && value) {
        return new Date(`1970-01-01T${value}Z`) < new Date(`1970-01-01T${closingTime}Z`);
      }
      return true;
    }),

  'closingTime-timeSlot-1': yup
    .string()
    .required('Closing time is required')
    .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
    .test('is-after-opening', 'Closing time must be after opening time', function (value) {
      const { openingTime } = this.parent;
      if (openingTime && value) {
        return new Date(`1970-01-01T${value}Z`) > new Date(`1970-01-01T${openingTime}Z`);
      }
      return true;
    }),

  'daysOfWeek-timeSlot-1': yup.array().min(1, 'At least one day should be selected'),
});

const generateValidationSchema = (values) => {
  const schemaShape = {
    'openingTime-timeSlot-1': yup
      .string()
      .required('Opening time is required')
      .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
      .test('is-before-closing', 'Opening time must be before closing time', function (value) {
        const { closingTime } = this.parent;
        if (closingTime && value) {
          return new Date(`1970-01-01T${value}Z`) < new Date(`1970-01-01T${closingTime}Z`);
        }
        return true;
      }),

    'closingTime-timeSlot-1': yup
      .string()
      .required('Closing time is required')
      .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
      .test('is-after-opening', 'Closing time must be after opening time', function (value) {
        const { openingTime } = this.parent;
        if (openingTime && value) {
          return new Date(`1970-01-01T${value}Z`) > new Date(`1970-01-01T${openingTime}Z`);
        }
        return true;
      }),

    'daysOfWeek-timeSlot-1': yup.array().min(1, 'At least one day should be selected'),
  };

  Object.keys(values).forEach((key) => {
    // Opening & closing time pair validation
    const match = key.match(/^openingTime-timeSlot(-\d+)?$/);
    if (match) {
      const suffix = match[1] || "";
      const openingKey = `openingTime-timeSlot${suffix}`;
      const closingKey = `closingTime-timeSlot${suffix}`;

      schemaShape[openingKey] = yup
        .string()
        .required("Opening time is required")
        .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
        .test(
          "is-before-closing",
          "Opening time must be before closing time",
          function (value) {
            const closingValue = this.parent[closingKey];
            if (value && closingValue) {
              return (
                new Date(`1970-01-01T${value}Z`) <
                new Date(`1970-01-01T${closingValue}Z`)
              );
            }
            return true;
          }
        );

      schemaShape[closingKey] = yup
        .string()
        .required("Closing time is required")
        .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
        .test(
          "is-after-opening",
          "Closing time must be after opening time",
          function (value) {
            const openingValue = this.parent[openingKey];
            if (value && openingValue) {
              return (
                new Date(`1970-01-01T${value}Z`) >
                new Date(`1970-01-01T${openingValue}Z`)
              );
            }
            return true;
          }
        );
    }

    // Days of the week array
    const dayMatch = key.match(/^daysOfWeek-timeSlot(-\d+)?$/);
    if (dayMatch) {
      schemaShape[key] = yup
        .array()
        .min(1, "At least one day should be selected");
    }
  });

  return yup.object().shape(schemaShape);
};

const Opening = ({ sectionData, details }) => {
    const [timeSlots, setTimeSlots] = useState([]);
    const currentUserId = localStorage.getItem("LPCurrentUserId");
    const [validationSchema, setValidationSchema] = useState(() => defaultValidationSchema);

    const resolver = useMemo(() => yupResolver(validationSchema), [validationSchema]);
    const form = useForm({
        resolver,
        mode: "onBlur",
    });
    const { register, handleSubmit, control, formState: { errors }, watch, reset, setValue } = form;
    const flattenFields = (fields = []) => {
        return fields.flatMap(field => {
            if (field.fields) {
                return flattenFields(field.fields);
            }
            return field;
        });
    };

    const allFields = flattenFields(sectionData.fields);

    const openingTimeField = allFields.find(field => field.id === "openingTime");
    const closingTimeField = allFields.find(field => field.id === "closingTime");
    const daysOfWeekField = allFields.find(field => field.id === "daysOfWeek");
    const addTimeSlotLinkField = allFields.find(field => field.id === "addTimeSlotLink");

    const handleAddTimeSlot = () => {
        setTimeSlots((prev) => [
            ...prev,
            {
                id: `timeSlot-${prev.length + 1}`,
                from: "",
                to: "",
                days: [],
            }
        ]);
        form.setValue(`openingTime-timeSlot-${timeSlots.length + 1}`, "");
        form.setValue(`closingTime-timeSlot-${timeSlots.length + 1}`, "");
        form.setValue(`daysOfWeek-timeSlot-${timeSlots.length + 1}`, []);
        const currentValues = form.getValues();
        const newSchema = generateValidationSchema(currentValues);
        setValidationSchema(newSchema); // This will re-trigger useForm with new resolver
    };

    const handleDeleteTimeSlot = (id) => {
        setTimeSlots((prev) => prev.filter(slot => slot.id !== id));
    };
    
    useEffect(() => {
        if (details?.data?.opening_hours?.length) {
            const slots = details.data.opening_hours.map((item, index) => ({
                id: `timeSlot-${index + 1}`,
                from: item.from,
                to: item.to,
                days: item.days,
            }));

            setTimeSlots(slots);
            const defaultValues = {};
            slots.forEach((slot) => {
                defaultValues[`openingTime-${slot.id}`] = slot.from;
                defaultValues[`closingTime-${slot.id}`] = slot.to;
                defaultValues[`daysOfWeek-${slot.id}`] = slot.days;
            });

            const newSchema = generateValidationSchema(defaultValues);
            setValidationSchema(newSchema); // This will re-trigger useForm with new resolver
            reset(defaultValues);
        }
    }, [details]);

    const openingHours = [];

    const onSubmit = async (data) => {
        Object.keys(data).forEach((key) => {
            const match = key.match(/^openingTime-timeSlot-(\d+)$/);
            if (match) {
                const index = match[1];
                const openingTime = data[`openingTime-timeSlot-${index}`];
                const closingTime = data[`closingTime-timeSlot-${index}`];
                const daysOfWeek = data[`daysOfWeek-timeSlot-${index}`];

                if (openingTime && closingTime && daysOfWeek) {
                    openingHours.push({
                        days: daysOfWeek,
                        from: openingTime,
                        to: closingTime,
                    });
                }
            }
        });
        const payload = {
            btn_submit: true,
            listings: [
                {
                    listing_id: details?.ID,
                    title: details?.post_title,
                    address_1: details?.data.address_1,
                    postcode: details?.data.postcode,
                    telephone_1: details?.data?.phone?.raw,
                    specialization: (Object.values(details.data.services || {})),
                    treatments: Object.values(details.data.treatments || {}),
                    provideruid: currentUserId,
                    opening_hours: openingHours,
                    uid: currentUserId
                }
            ]
        };
        try {
            const res = await axiosProxyInstance.post('/signup/2/', payload);
            cogoToast.success("Opening Times Updated Successfully!");
        } catch (err) {
            console.error("Submission failed:", err);
        }
    }

    return (
        <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box className='margin-top-15'>
                    <Box style={{ flex: "1" }}>

                        {timeSlots.map((slot, index) => (
                            <Box key={slot.id} mt={3}>
                                <Box className={styles.formFields}>
                                    <Box style={{ flex: "1" }}>
                                        <FormField
                                            field={{ ...openingTimeField, id: `${openingTimeField.id}-${slot.id}` }}
                                            register={register}
                                            control={control}
                                            errors={errors}
                                            watch={watch}
                                            form={form}
                                            setValue={setValue}
                                        />
                                    </Box>
                                    <Box style={{ flex: "1" }}>
                                        <FormField
                                            field={{ ...closingTimeField, id: `${closingTimeField.id}-${slot.id}` }}
                                            register={register}
                                            control={control}
                                            errors={errors}
                                            watch={watch}
                                            form={form}
                                            setValue={setValue}
                                        />
                                    </Box>
                                </Box>

                                <Box className={styles.openingFormFields}>
                                    <FormField
                                        field={{ ...daysOfWeekField, id: `${daysOfWeekField.id}-${slot.id}` }}
                                        register={register}
                                        control={control}
                                        errors={errors}
                                        watch={watch}
                                        form={form}
                                        setValue={setValue}
                                    />

                                    {index > 0 && (
                                        <Box mt={3}>
                                            <Button
                                                color="error"
                                                size="small"
                                                onClick={() => handleDeleteTimeSlot(slot.id)}
                                            >
                                                <RemoveCircleOutlineOutlinedIcon />
                                            </Button>
                                        </Box>
                                    )}
                                </Box>

                            </Box>
                        ))}
                        <FormField
                            field={addTimeSlotLinkField}
                            onLinkButtonClick={() => handleAddTimeSlot()}
                            register={register}
                            control={control}
                            errors={errors}
                        />


                        <Box className={styles.submitButtonContainer}>
                            <Button
                                className={styles.primaryButton}
                                size="large"
                                type="submit"
                            >
                                Save Changes
                            </Button>
                            <Button
                                className={styles.secondaryButton}
                                size="large"
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </form>
        </Box>
    )
}
export default Opening